// Ground materials (RALLY_GOAL.md §11): splatted terrain (forest floor, rock with triplanar mapping
// on steep faces, grass, dirt) and the road (hard-packed gravel with ruts, loose gravel, broken
// tarmac, mud and wet darkening, verges that match the terrain), all PBR from Poly Haven textures.
import * as THREE from 'three/webgpu';
import type { Node } from 'three/webgpu';
import { attribute, texture, positionWorld, normalWorld, vec2, vec3, float, mix, clamp, max, smoothstep, sqrt, dot, uv, vertexStage, mx_noise_float, normalMap, step, abs } from 'three/tsl';
import type { GameTextures, PbrSet } from './textures';
import { MatteNodeMaterial, setEnvScale } from './matte';

type V2 = Node<'vec2'>;
type V3 = Node<'vec3'>;
type F = Node<'float'>;

interface Layer {
  albedo: V3;
  /** Tangent-space normal XY in −1..1. */
  nxy: V2;
  rough: F;
  /** Pseudo-height for height-based blending (albedo luminance). */
  h: F;
}

function sample(set: PbrSet, coord: V2, withNormal = true): Layer {
  const a = texture(set.albedo, coord).rgb;
  const n = withNormal ? texture(set.nrm, coord) : null;
  return {
    albedo: a,
    nxy: n ? n.rg.mul(2).sub(1) : vec2(0, 0),
    rough: n ? n.b : float(0.85),
    h: dot(a, vec3(0.3, 0.55, 0.15)),
  };
}

/** Height-aware blend: sharper transitions where one layer's texture "rises" above another. */
function blend(layers: Layer[], weights: F[]): { albedo: V3; nxy: V2; rough: F } {
  const w = weights.map((wi, i) => {
    const ww = max(wi.mul(layers[i].h.add(0.35)), float(0));
    return ww.mul(ww).mul(ww);
  });
  let sum: F = w[0];
  for (let i = 1; i < w.length; i++) sum = sum.add(w[i]);
  sum = max(sum, float(1e-4));
  let albedo: V3 = layers[0].albedo.mul(w[0]);
  let nxy: V2 = layers[0].nxy.mul(w[0]);
  let rough: F = layers[0].rough.mul(w[0]);
  for (let i = 1; i < layers.length; i++) {
    albedo = albedo.add(layers[i].albedo.mul(w[i]));
    nxy = nxy.add(layers[i].nxy.mul(w[i]));
    rough = rough.add(layers[i].rough.mul(w[i]));
  }
  return { albedo: albedo.div(sum), nxy: nxy.div(sum), rough: rough.div(sum) };
}

function applyNormal(m: THREE.MeshStandardNodeMaterial, nxy: V2, strength: number): void {
  const xy = nxy.mul(strength);
  const z = sqrt(max(float(1).sub(dot(xy, xy)), float(0.05)));
  m.normalNode = normalMap(vec3(xy, z).mul(0.5).add(0.5));
}

/** Low-frequency variation computed per vertex (cheap) — breaks up tiling at a distance. */
function macroNoise(scale: number, offset = 0): F {
  return vertexStage(mx_noise_float(positionWorld.xz.mul(scale).add(offset)))
    .mul(0.5)
    .add(0.5);
}

/**
 * The terrain's ground cover blend, shared with the road verges so they meet without a seam.
 * `lite` (road verges): albedo only and no rock layer, to stay well inside the 16 textures per
 * shader stage that WebGPU and WebGL2 guarantee (the road already samples four surfaces).
 */
function groundCover(tx: GameTextures, w: V3, lite = false): { albedo: V3; nxy: V2; rough: F } {
  const p = positionWorld;
  const macro = macroNoise(0.012);
  const patch = macroNoise(0.07, 31.7);
  const forest = sample(tx.sets.forest_floor, p.xz.div(3.2), !lite);
  const grass = sample(tx.sets.grass, p.xz.div(2.6), !lite);
  const dirt = sample(tx.sets.mud, p.xz.div(3.0), !lite);
  if (lite) {
    const wF = w.x.mul(mix(float(0.8), float(1.2), patch));
    const wG = w.z.mul(mix(float(1.2), float(0.8), patch)).add(w.y);
    const wD = max(float(1).sub(w.x).sub(w.y).sub(w.z), float(0));
    dirt.albedo = dirt.albedo.mul(vec3(1.12, 1.06, 0.98));
    // Same heather patches and canopy shade as the terrain, so the verge meets it without a seam.
    const heatherL = smoothstep(float(0.55), float(0.78), macroNoise(0.045, 91.3));
    grass.albedo = mix(grass.albedo, grass.albedo.mul(vec3(0.8, 0.62, 0.62)), heatherL.mul(0.8));
    const b = blend([forest, grass, dirt], [wF, wG, wD]);
    const tint = mix(vec3(0.88, 0.9, 0.86), vec3(1.08, 1.04, 0.98), macro);
    const canopyL = mix(float(1), float(0.62), clamp(w.x, 0, 1));
    return { albedo: b.albedo.mul(tint).mul(canopyL), nxy: vec2(0, 0), rough: float(0.9) };
  }
  // Rock: triplanar on steep faces (biased projection weights from the world normal).
  const nW = abs(normalWorld);
  const tw = nW.mul(nW).mul(nW).mul(nW);
  const tws = tw.x.add(tw.y).add(tw.z);
  const rs = tx.sets.rock;
  const rockA = texture(rs.albedo, p.zy.div(5.5))
    .rgb.mul(tw.x)
    .add(texture(rs.albedo, p.xz.div(5.5)).rgb.mul(tw.y))
    .add(texture(rs.albedo, p.xy.div(5.5)).rgb.mul(tw.z))
    .div(tws);
  const rockN = texture(rs.nrm, p.xz.div(5.5));
  const rock: Layer = { albedo: rockA, nxy: rockN.rg.mul(2).sub(1).mul(tw.y.div(tws)), rough: rockN.b, h: dot(rockA, vec3(0.3, 0.55, 0.15)).add(0.15) };
  // Dirt reads as dry earth, not wet mud.
  dirt.albedo = dirt.albedo.mul(vec3(1.12, 1.06, 0.98));
  // Heather and dead grass patches on open ground.
  const heather = smoothstep(float(0.55), float(0.78), macroNoise(0.045, 91.3));
  grass.albedo = mix(grass.albedo, grass.albedo.mul(vec3(0.8, 0.62, 0.62)), heather.mul(0.8));
  dirt.rough = clamp(dirt.rough.add(0.15), 0, 1);
  const wForest = w.x.mul(mix(float(0.8), float(1.2), patch));
  const wGrass = w.z.mul(mix(float(1.2), float(0.8), patch));
  const wRock = w.y;
  const wDirt = max(float(1).sub(w.x).sub(w.y).sub(w.z), float(0));
  const b = blend([forest, rock, grass, dirt], [wForest, wRock, wGrass, wDirt]);
  // Macro tint: a few percent of brightness/hue drift; the floor under the canopy is darker.
  const tint = mix(vec3(0.88, 0.9, 0.86), vec3(1.08, 1.04, 0.98), macro);
  const canopy = mix(float(1), float(0.62), clamp(w.x, 0, 1));
  return { albedo: b.albedo.mul(tint).mul(canopy), nxy: b.nxy, rough: b.rough };
}

export function terrainMaterial(tx: GameTextures): THREE.MeshStandardNodeMaterial {
  const m = new MatteNodeMaterial({ metalness: 0 });
  const g = groundCover(tx, attribute('color', 'vec3') as unknown as V3);
  m.colorNode = g.albedo;
  // Natural ground is very rough; low values make backlit grass glare white at grazing angles.
  m.roughnessNode = clamp(g.rough.mul(0.5).add(0.5), 0.86, 1);
  applyNormal(m, g.nxy, 1.0);
  // Less sky in the ground: under the canopy and at grazing angles the environment's specular
  // turned the dark forest floor a pale, snowy blue.
  setEnvScale(m, 0.55);
  return m;
}

/**
 * Road ribbon. color: r = on-road (0 verge, 0.45 shoulder, 1 road, 2 tarmac), g = loose gravel,
 * b = wet/mud; splat = the terrain's ground-cover weights at that point (for the verges).
 * uv = (lateral offset, distance) / 3 m.
 */
export function roadMaterial(tx: GameTextures): THREE.MeshStandardNodeMaterial {
  const m = new MatteNodeMaterial({ metalness: 0 });
  const c = attribute('color', 'vec3') as unknown as V3;
  const sp = attribute('splat', 'vec3') as unknown as V3;
  const ruv = uv();
  const lateral = ruv.x.mul(3);
  const tarmacW = step(float(1.5), c.x);
  const onRoad = clamp(c.x, 0, 1);
  const macro = macroNoise(0.02, 7.1);
  const hard = sample(tx.sets.gravel_road, ruv.mul(vec2(1.0, 1.0)));
  const loose = sample(tx.sets.gravel_loose, ruv.mul(vec2(1.6, 1.6)), false);
  // Loose stone (centre ridge, edges, windrows): darker and coarser than the compacted tracks.
  loose.albedo = loose.albedo.mul(0.86);
  loose.nxy = hard.nxy.mul(0.9);
  const tarmac = sample(tx.sets.tarmac, ruv.mul(vec2(0.9, 0.9)));
  // Weathered grey tarmac (the source is a brownish damaged road): mostly desaturated.
  tarmac.albedo = mix(vec3(dot(tarmac.albedo, vec3(0.3, 0.55, 0.15))), tarmac.albedo, float(0.25)).mul(0.82);
  const mud = sample(tx.sets.mud, ruv.mul(vec2(1.1, 1.1)), false);
  mud.nxy = hard.nxy.mul(0.4);
  // Tyre ruts (±0.82 m): compacted, darker, less loose stone.
  const rut = smoothstep(float(0.42), float(0.05), abs(abs(lateral).sub(0.82)));
  const looseW = c.y.mul(onRoad).mul(float(1).sub(rut.mul(0.8))).mul(mix(float(0.7), float(1.3), macro));
  const wTarmac = tarmacW;
  const wLoose = looseW.mul(float(1).sub(tarmacW));
  const wHard = onRoad.mul(float(1).sub(tarmacW)).mul(float(1).sub(clamp(looseW, 0, 1)));
  const wWet = c.z;
  const road = blend([hard, loose, tarmac, mud], [wHard.add(0.001), wLoose, wTarmac.mul(2), wWet.mul(1.4)]);
  // Wheel tracks: compacted, smoother and a touch lighter.
  const compact = float(1).add(rut.mul(0.08).mul(onRoad).mul(float(1).sub(tarmacW)));
  const roadLum = dot(road.albedo, vec3(0.3, 0.55, 0.15));
  let albedo: V3 = mix(vec3(roadLum), road.albedo, float(0.6)).mul(compact).mul(vec3(1.03, 1.0, 0.94));
  // Verges: same ground cover as the terrain (seamless edge), blended by the on-road weight.
  const verge = groundCover(tx, sp, true);
  const roadAmt = smoothstep(float(0.05), float(0.5), onRoad);
  albedo = mix(verge.albedo, albedo, roadAmt);
  let rough: F = mix(verge.rough, road.rough, roadAmt);
  const nxy = mix(verge.nxy, road.nxy, roadAmt);
  // Wet darkening and sheen (mud, puddles, the ford).
  const wet = clamp(c.z.mul(1.1), 0, 1);
  albedo = albedo.mul(mix(float(1), float(0.55), wet));
  rough = mix(rough, float(0.28), wet.mul(0.85));
  m.colorNode = albedo;
  m.roughnessNode = clamp(rough.mul(0.6).add(0.42), 0.3, 1).max(float(0.8).mul(float(1).sub(wet)));
  // Puddles and wet mud keep their full grazing reflection.
  m.f90Node = mix(float(0.2), float(1), wet);
  applyNormal(m, nxy, 0.6);
  setEnvScale(m, 0.55);
  return m;
}

/** Pothole / washout decal: damp dark earth fading out at the rim (uv.x radial, uv.y water). */
export function damageDecalMaterial(): THREE.MeshStandardNodeMaterial {
  const m = new MatteNodeMaterial({ transparent: true, depthWrite: false, metalness: 0 });
  const q = uv().x;
  const rim = smoothstep(float(0.75), float(1.05), q);
  const grain = mx_noise_float(positionWorld.xz.mul(9)).mul(0.5).add(0.5);
  const inner = vec3(0.075, 0.06, 0.045).mul(mix(float(0.75), float(1.15), grain));
  const edge = vec3(0.16, 0.13, 0.1);
  m.colorNode = mix(inner, edge, rim);
  m.opacityNode = float(1).sub(smoothstep(float(0.95), float(1.3), q)).mul(0.92);
  m.roughnessNode = mix(float(0.55), float(0.95), rim);
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.polygonOffsetUnits = -2;
  return m;
}

export function waterMaterial(): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ color: 0x2c3b36, roughness: 0.06, metalness: 0, transparent: true, opacity: 0.86 });
  const ripple = mx_noise_float(positionWorld.xz.mul(1.7)).mul(0.08);
  m.normalNode = normalMap(vec3(ripple, ripple.mul(0.6), float(1)).mul(0.5).add(0.5));
  return m;
}
